import React, { useState } from 'react';
import { ArrowUpIcon, ArrowDownIcon, FileTextIcon, ShoppingCartIcon, ReceiptIcon, FileIcon, DollarSignIcon, CreditCardIcon } from 'lucide-react';
import { LedgerEntry, LedgerSourceModule } from './LedgerView';
import { formatCurrency } from '../utils/currencyFormatter';
interface LedgerTableProps {
  entries: LedgerEntry[];
  onSelectEntry: (entry: LedgerEntry) => void;
  locale: string;
}
export function LedgerTable({
  entries,
  onSelectEntry,
  locale
}: LedgerTableProps) {
  const [sortField, setSortField] = useState<keyof LedgerEntry>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // Handle sort
  const handleSort = (field: keyof LedgerEntry) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Sort entries
  const sortedEntries = [...entries].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    // Handle null values
    if (aValue === null) aValue = 0;
    if (bValue === null) bValue = 0;
    // String comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    // Number comparison
    return sortDirection === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
  });
  // Get source module icon
  const getSourceModuleIcon = (module: LedgerSourceModule) => {
    switch (module) {
      case 'RapidAP':
        return <ReceiptIcon className="h-4 w-4 text-purple-500" data-id="element-8259" />;
      case 'RapidPO':
        return <ShoppingCartIcon className="h-4 w-4 text-blue-500" data-id="element-8260" />;
      case 'RapidExpense':
        return <CreditCardIcon className="h-4 w-4 text-green-500" data-id="element-8261" />;
      case 'RapidContract':
        return <FileTextIcon className="h-4 w-4 text-orange-500" data-id="element-8262" />;
      case 'RapidPayment':
        return <DollarSignIcon className="h-4 w-4 text-indigo-500" data-id="element-8263" />;
      case 'Manual':
        return <FileIcon className="h-4 w-4 text-gray-500" data-id="element-8264" />;
      default:
        return <FileIcon className="h-4 w-4 text-gray-500" data-id="element-8265" />;
    }
  };
  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Draft':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-8266">
            Draft
          </span>;
      case 'Posted':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-8267">
            Posted
          </span>;
      case 'Reversed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-8268">
            Reversed
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-8269">
            {status}
          </span>;
    }
  };
  // Column header with sort capability
  const SortableHeader = ({
    field,
    label
  }: {
    field: keyof LedgerEntry;
    label: string;
  }) => <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50" onClick={() => handleSort(field)} data-id="element-8270">
      <div className="flex items-center space-x-1" data-id="element-8271">
        <span data-id="element-8272">{label}</span>
        {sortField === field ? sortDirection === 'asc' ? <ArrowUpIcon className="h-3 w-3" data-id="element-8273" /> : <ArrowDownIcon className="h-3 w-3" data-id="element-8274" /> : null}
      </div>
    </th>;
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-8275">
      <div className="overflow-x-auto" data-id="element-8276">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-8277">
          <thead className="bg-gray-50 sticky top-0" data-id="element-8278">
            <tr data-id="element-8279">
              <SortableHeader field="date" label="Date" data-id="element-8280" />
              <SortableHeader field="sourceModule" label="Source" data-id="element-8281" />
              <SortableHeader field="sourceReference" label="Reference" data-id="element-8282" />
              <SortableHeader field="description" label="Description" data-id="element-8283" />
              <SortableHeader field="glCode" label="GL Code" data-id="element-8284" />
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8285">
                Debit
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8286">
                Credit
              </th>
              <SortableHeader field="currency" label="Currency" data-id="element-8287" />
              <SortableHeader field="status" label="Status" data-id="element-8288" />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-8289">
            {sortedEntries.length === 0 ? <tr data-id="element-8290">
                <td colSpan={9} className="px-6 py-4 text-center text-gray-500" data-id="element-8291">
                  No ledger entries found matching your criteria.
                </td>
              </tr> : sortedEntries.map(entry => <tr key={entry.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onSelectEntry(entry)} data-id="element-8292">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-8293">
                    {new Date(entry.date).toLocaleDateString(locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-8294">
                    <div className="flex items-center" data-id="element-8295">
                      {getSourceModuleIcon(entry.sourceModule)}
                      <span className="ml-1" data-id="element-8296">
                        {entry.sourceModule.replace('Rapid', '')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-8297">
                    {entry.sourceReference}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate" data-id="element-8298">
                    {entry.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono" data-id="element-8299">
                    {entry.glCode}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono" data-id="element-8300">
                    {entry.debit ? formatCurrency(entry.debit, entry.currency, locale) : ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono" data-id="element-8301">
                    {entry.credit ? formatCurrency(entry.credit, entry.currency, locale) : ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-8302">
                    {entry.currency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-8303">
                    {getStatusBadge(entry.status)}
                  </td>
                </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}